import React from "react";
import PropTypes from "prop-types";
import { Link, useHistory } from "react-router-dom";
import {
  Card,
  Box,
  Typography,
  Grid,
  Stack,
} from "@mui/material";
import {
  Sparkles,
  ShieldCheck,
  Zap,
  Layers,
  ArrowRight,
  HelpCircle,
  Users,
} from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { CommonButton } from "../../../../Components/ui";
import { Path, LOTTERY_NAMES } from "../../../../../static/Constants";

const AboutHero = ({ onScrollToSection }) => {
  const history = useHistory();

  return (
    <Card
      sx={{
        p: { xs: 3, sm: 4.5 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        position: "relative",
        overflow: "hidden",
        mb: 4,
      }}
    >
      {/* Top Accent Gradient Stripe */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: colors.gradients.primary,
        }}
      />

      <Grid container spacing={4} alignItems="center">
        {/* Left Side: Headline, Vision Statement & Action Buttons */}
        <Grid item xs={12} lg={7}>
          <Stack spacing={2.5}>
            {/* Tag Badge */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.75,
                  py: 0.6,
                  borderRadius: borderRadiusPx.full,
                  bgcolor: colors.blue[50],
                  border: `1px solid ${colors.blue[100]}`,
                  color: colors.primary.main,
                }}
              >
                <Sparkles size={16} color={colors.primary.main} />
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.primary.main,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    fontSize: "0.75rem",
                  }}
                >
                  Web3 Decentralized Platform
                </Typography>
              </Box>
            </Box>

            {/* Main Heading */}
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                color: colors.slate[900],
                letterSpacing: "-0.75px",
                fontSize: { xs: "1.85rem", sm: "2.4rem", md: "2.75rem" },
                lineHeight: 1.15,
              }}
            >
              Decentralized Lottery Ecosystem
            </Typography>

            {/* Description Subtitle */}
            <Typography
              variant="body1"
              sx={{
                color: colors.slate[600],
                fontSize: { xs: "0.95rem", sm: "1.05rem" },
                lineHeight: 1.65,
                maxWidth: 640,
              }}
            >
              AutoBET is an online lottery platform fantasy of the ultimate lottery platform.
              Powered by immutable smart contracts, verifiable Chainlink VRF random draws, and non-custodial prize distributions,
              it offers a trustless and transparent gaming experience for participants worldwide.
            </Typography>

            {/* Action Buttons */}
            <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ gap: 1.5, pt: 1 }}>
              <CommonButton
                variant="primary"
                size="medium"
                component={Link}
                to={Path.allLotteries}
                onClick={() => history.push(Path.allLotteries)}
                endIcon={<ArrowRight size={16} />}
              >
                Explore Lotteries
              </CommonButton>

              <CommonButton
                variant="outline"
                size="medium"
                component={Link}
                to={Path.registerasorg}
                onClick={() => history.push(Path.registerasorg)}
                startIcon={<Users size={16} />}
              >
                Creator Studio
              </CommonButton>

              <CommonButton
                variant="outline"
                size="medium"
                component={Link}
                to={Path.help}
                onClick={() => history.push(Path.help)}
                startIcon={<HelpCircle size={16} />}
              >
                Help & Guides
              </CommonButton>
            </Stack>
          </Stack>
        </Grid>

        {/* Right Side: Key Pillars Highlights Grid */}
        <Grid item xs={12} lg={5}>
          <Grid container spacing={2}>
            {/* Highlight 1: 100% On-Chain */}
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 2.25,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.blue[50],
                  border: `1px solid ${colors.blue[200]}`,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.primary.main,
                      color: colors.neutral.white,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Layers size={16} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.primary.dark,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Blockchain
                  </Typography>
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 900, color: colors.primary.dark, fontSize: "1.25rem", mb: 0.25 }}>
                  100% On-Chain
                </Typography>
                <Typography variant="caption" sx={{ color: colors.slate[600], fontWeight: 500, lineHeight: 1.4 }}>
                  Immutable public audit trail for every draw
                </Typography>
              </Box>
            </Grid>

            {/* Highlight 2: Chainlink VRF */}
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 2.25,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.emerald[50],
                  border: `1px solid ${colors.emerald[200]}`,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.emerald[600],
                      color: colors.neutral.white,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ShieldCheck size={16} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.emerald[800],
                      fontWeight: 800,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Verifiable
                  </Typography>
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 900, color: colors.emerald[800], fontSize: "1.25rem", mb: 0.25 }}>
                  Chainlink VRF
                </Typography>
                <Typography variant="caption" sx={{ color: colors.slate[600], fontWeight: 500, lineHeight: 1.4 }}>
                  Tamper-proof verifiable random numbers
                </Typography>
              </Box>
            </Grid>

            {/* Highlight 3: Instant Payouts */}
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 2.25,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.purple[50],
                  border: `1px solid ${colors.purple[200]}`,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.purple[600],
                      color: colors.neutral.white,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Zap size={16} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.purple[800],
                      fontWeight: 800,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Automated
                  </Typography>
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 900, color: colors.purple[800], fontSize: "1.25rem", mb: 0.25 }}>
                  Instant Payouts
                </Typography>
                <Typography variant="caption" sx={{ color: colors.slate[600], fontWeight: 500, lineHeight: 1.4 }}>
                  Automated transfers to winner wallets
                </Typography>
              </Box>
            </Grid>

            {/* Highlight 4: Multi-Engine Lotteries */}
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 2.25,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.amber[50],
                  border: `1px solid ${colors.amber[200]}`,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.amber[600],
                      color: colors.neutral.white,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Sparkles size={16} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.amber[800],
                      fontWeight: 800,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Game Suite
                  </Typography>
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 900, color: colors.amber[800], fontSize: "1.25rem", mb: 0.25 }}>
                  4 Game Engines
                </Typography>
                <Typography variant="caption" sx={{ color: colors.slate[600], fontWeight: 500, lineHeight: 1.4 }}>
                  {`${LOTTERY_NAMES.MEGABET}, ${LOTTERY_NAMES.SPINBET}, ${LOTTERY_NAMES.MINEBET}, ${LOTTERY_NAMES.ROCKETBET}`}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};

AboutHero.propTypes = {
  onScrollToSection: PropTypes.func,
};

export default AboutHero;
